/**
 * Señales de que un mensaje viene de la infraestructura y no está escrito para
 * quien usa el sistema: protocolos, credenciales, hosts, stack traces, errores
 * de JavaScript.
 *
 * Existe porque los backends reenvían tal cual el error de la librería que
 * falló, y eso terminaba en pantalla: un fallo de envío de mail mostraba
 * "EAUTH - Invalid login: 535-5.7.8 Username and Password not accepted ...
 * gsmtp", que le cuenta al usuario qué proveedor de correo se usa y que sus
 * credenciales están mal, sin darle nada que pueda hacer al respecto.
 */
const TECHNICAL_DETAIL_PATTERN = new RegExp(
  [
    // Servicios y librerías de infraestructura.
    '\\b(smtp|imap|eauth|gsmtp|nodemailer|keycloak|sequelize|mongo(db)?|redis|amqp|rabbit(mq)?|postgres(ql)?|axios|nginx)\\b',
    // Códigos de error de red de Node.
    '\\bE(CONNREFUSED|CONNRESET|CONNABORTED|NOTFOUND|TIMEDOUT|PIPE|HOSTUNREACH|AI_AGAIN)\\b',
    // Fallos de transporte y autenticación de servicios.
    '\\b(invalid login|authentication failed|socket hang up|getaddrinfo|self signed certificate|bad credentials)\\b',
    // Códigos de respuesta SMTP tipo "535-5.7.8".
    '\\b5\\d{2}[- ]\\d\\.\\d\\.\\d\\b',
    // Errores de JavaScript que se escaparon del front o del back.
    '\\b(cannot read propert|is not a function|is not defined|undefined is not|null is not)',
    // Nombres de clase de error de cualquier librería: TypeError,
    // SequelizeUniqueConstraintError, MongoServerError, AxiosError...
    '\\b[A-Z][A-Za-z]{2,}Error\\b',
    // Stack traces y respuestas HTML de un proxy.
    '\\n\\s*at\\s+',
    '<html',
  ].join('|'),
  'i'
);

/** true si el texto delata infraestructura y no debería llegar a la pantalla. */
export function isTechnicalErrorMessage(message: string): boolean {
  return TECHNICAL_DETAIL_PATTERN.test(message);
}

/** Aplana lo que el backend haya puesto en `message`: string o array de strings. */
const normalizeMessage = (candidate: unknown): string | null => {
  const raw = Array.isArray(candidate)
    ? candidate.filter((item) => typeof item === 'string').join('. ')
    : typeof candidate === 'string'
      ? candidate
      : null;

  const trimmed = raw?.trim();
  return trimmed ? trimmed : null;
};

/**
 * Devuelve el mensaje del backend solo si es presentable; si no, el fallback
 * que ya trae cada pantalla. El detalle técnico no se pierde: va a la consola,
 * que es donde sirve para diagnosticar.
 */
const safeMessage = (candidate: unknown, fallback: string): string => {
  const text = normalizeMessage(candidate);
  if (!text) return fallback;

  if (isTechnicalErrorMessage(text)) {
    console.error('[Detalle técnico oculto al usuario]', text);
    return fallback;
  }

  return text;
};

export function getRegisterErrorMessage(
  error: unknown,
  defaultMessage: string
): string {
  const rawMessage = getErrorMessage(error, defaultMessage);

  if (isTechnicalErrorMessage(rawMessage)) {
    return 'No se pudo crear el usuario: verifica que la contraseña incluya mayúscula, minúscula, número y algún carácter especial, tenga al menos 8 caracteres, y que el email y el teléfono sean correctos.';
  }

  return rawMessage;
}

export function getErrorMessage(error: any, defaultMessage: string): string {
  const backendMessage = error?.data?.error ?? error?.data?.message;

  if (error?.status === 400) {
    return safeMessage(backendMessage, defaultMessage);
  } else if (error?.status === 401 || error?.status === 403) {
    return 'No tiene permisos para realizar esta acción o su sesión ha expirado.';
  } else if (error?.status === 500) {
    return safeMessage(
      backendMessage,
      'Error interno del servidor. Por favor, intente nuevamente más tarde.'
    );
  } else if (error?.status === 502 || error?.status === 503) {
    return 'El servicio no está disponible en este momento. Por favor, intente más tarde.';
  } else if (
    error?.status === 'FETCH_ERROR' ||
    error?.code === 'ECONNABORTED'
  ) {
    return 'El servicio no respondió a tiempo. Por favor, intente nuevamente.';
  } else if (backendMessage) {
    return safeMessage(backendMessage, defaultMessage);
  } else if (error?.error) {
    return safeMessage(error.error, defaultMessage);
  } else if (error?.message) {
    return safeMessage(error.message, defaultMessage);
  }

  return defaultMessage;
}


const ADD_SHIPPINGS_ERROR_MAP: Record<string, string> = {
  INVALID_STATUS:
    'El pedido no se puede agregar porque su estado no permite rutearlo',
  ALREADY_IN_ROUTE: 'El pedido ya está asignado a esta ruta',
  DIFFERENT_OPERATOR: 'El pedido no pertenece al operador de la ruta',
  ROUTE_CLOSED: 'No se pueden agregar pedidos a una ruta finalizada o cancelada',
};

export function mapAddShippingsError(error: unknown, defaultMessage: string): string {
  const err = error as { data?: { code?: string; error?: string; message?: string } };
  const code = err?.data?.code;
  if (code && ADD_SHIPPINGS_ERROR_MAP[code]) {
    return ADD_SHIPPINGS_ERROR_MAP[code];
  }
  return getErrorMessage(error, defaultMessage);
}


/* -------------------------------------------------------------------------- */
/*                            Errores de autenticación                        */
/* -------------------------------------------------------------------------- */

/**
 * Statuses con los que RTK Query avisa que nunca hubo una respuesta HTTP útil:
 * el servicio no contestó, cortó por timeout, o devolvió algo que no es JSON
 * (típico del HTML de error de nginx cuando el back está caído).
 */
const TRANSPORT_ERROR_STATUSES = [
  'FETCH_ERROR',
  'TIMEOUT_ERROR',
  'PARSING_ERROR',
  'CUSTOM_ERROR',
];

/** Statuses en los que el mensaje del backend está pensado para el usuario. */
const USER_FACING_AUTH_STATUSES = [400, 401, 403, 409, 423];

export const AUTH_SYSTEM_DOWN_MESSAGE =
  'El sistema de inicio de sesión no está funcionando en este momento. Aguardá unos minutos y volvé a intentar; si el problema continúa, contactá a TI.';

const USER_INFO_UNAVAILABLE_MESSAGE =
  'Tus credenciales son correctas, pero no se pudo cargar tu usuario porque el sistema no está respondiendo. Aguardá unos minutos y volvé a intentar; si el problema continúa, contactá a TI.';

const DEFAULT_CREDENTIALS_MESSAGE =
  'No pudimos iniciar tu sesión. Verificá el email y la contraseña e intentá de nuevo.';

const TOO_MANY_ATTEMPTS_MESSAGE =
  'Demasiados intentos seguidos. Esperá unos minutos antes de volver a probar.';

const readBackendMessage = (error: unknown): string | null => {
  const data = (error as { data?: unknown })?.data;
  if (!data || typeof data !== 'object') return null;

  const body = data as { message?: unknown; error?: unknown };
  const raw = Array.isArray(body.message)
    ? body.message.filter((item) => typeof item === 'string').join('. ')
    : typeof body.message === 'string'
      ? body.message
      : typeof body.error === 'string'
        ? body.error
        : null;

  const trimmed = raw?.trim();
  if (!trimmed) return null;
  // Mismo criterio que el resto de la app: la infraestructura no se muestra.
  if (isTechnicalErrorMessage(trimmed)) return null;

  return trimmed;
};

/** Etapa del login: el token se pide a un servicio y los datos del usuario a otro. */
export type AuthStage = 'login' | 'userInfo';

/**
 * Traduce un error de login a algo que el usuario pueda accionar.
 *
 * Distingue dos mundos: cuando el backend contestó con un 4xx su mensaje ya está
 * escrito para el usuario ("tu cuenta no tiene roles asignados") y se muestra tal
 * cual; cuando no hubo respuesta, o fue un 5xx, el usuario no tiene nada que
 * corregir y lo único útil es decirle que el sistema no está y a quién avisar.
 *
 * Nunca accede a `error.data.message` a ciegas: sin respuesta HTTP no existe
 * `data`, y ese acceso es justamente lo que terminaba mostrando
 * "Cannot read properties of undefined (reading 'message')" como si fuera el
 * motivo del fallo.
 */
export function getLoginErrorMessage(
  error: unknown,
  stage: AuthStage = 'login'
): string {
  // Las credenciales ya fueron aceptadas: si falla acá, el usuario no tiene
  // nada que corregir de su lado.
  if (stage === 'userInfo') {
    return USER_INFO_UNAVAILABLE_MESSAGE;
  }

  const status = (error as { status?: unknown })?.status;

  if (typeof status === 'string' && TRANSPORT_ERROR_STATUSES.includes(status)) {
    return AUTH_SYSTEM_DOWN_MESSAGE;
  }

  if (typeof status === 'number') {
    if (status === 429) {
      return readBackendMessage(error) ?? TOO_MANY_ATTEMPTS_MESSAGE;
    }
    // 404 en el endpoint de login es ruteo mal configurado, no credenciales:
    // el backend traduce el 404 de credenciales a 401 antes de responder.
    if (status === 404 || status >= 500) {
      return AUTH_SYSTEM_DOWN_MESSAGE;
    }
    if (USER_FACING_AUTH_STATUSES.includes(status)) {
      return readBackendMessage(error) ?? DEFAULT_CREDENTIALS_MESSAGE;
    }
    return readBackendMessage(error) ?? AUTH_SYSTEM_DOWN_MESSAGE;
  }

  // Sin status reconocible: CORS, un TypeError del propio front, o cualquier
  // cosa que no llegó a ser una respuesta.
  return AUTH_SYSTEM_DOWN_MESSAGE;
}
